import { inject, Injectable, OnDestroy } from '@angular/core';
import { NgInject } from '@df/ng/ng-inject';
import { ProcessorService } from '@df/ng/processor.service';
import { ICmsDataChangeService, NICmsDataChange } from '@icms/core/data/change/icms-data-change.service';
import { ICmsCoreIntegrationComponentService } from '@icms/core/integration/icms-core-integration-component.service';
import { ICmsCoreIntegrationProviderService } from '@icms/core/integration/icms-core-integration-provider.service';
import { BehaviorSubject, Subject, Subscription } from 'rxjs';
import { NICmsContentComponent } from '../../interfaces/icms-content-component.interfaces';
import { ICmsContentComponentValidationService, IValidtionEvent, ObjectHook } from '../icms-content-component-validation.service';

export interface ICmsProviderStorage<T> {
    subscription?: Subscription;
    errorSubscription?: Subscription;

    componentData$: BehaviorSubject<T | undefined>;
    iCmsValidation$: Subject<IValidtionEvent | undefined | void>;

    contentMeta: NICmsContentComponent.IContentMeta;
    propertyKey: string | symbol;
}

export abstract class ICmsProviderAbstract<T, TStorage extends ICmsProviderStorage<T> = ICmsProviderStorage<T>> {
    @NgInject(ICmsDataChangeService)
    private iCmsDataChangeService!: ICmsDataChangeService;

    @NgInject(ICmsCoreIntegrationProviderService)
    private iCmsCoreIntegrationProviderService!: ICmsCoreIntegrationProviderService;

    @NgInject(ICmsCoreIntegrationComponentService)
    private iCmsCoreIntegrationComponentService!: ICmsCoreIntegrationComponentService;

    @NgInject(ICmsContentComponentValidationService)
    private iCmsContentComponentValidationService!: ICmsContentComponentValidationService;

    readonly storage = new Map<NICmsContentComponent.IComponent, TStorage>();

    constructor(
        readonly contentMeta: NICmsContentComponent.IContentMeta,
        readonly target: NICmsContentComponent.IComponent,
        readonly propertyKey: string | symbol
    ) {
        const set = (data, instance: NICmsContentComponent.IComponent) => this.setContent(data, instance);
        const getComponentData$ = (instance: NICmsContentComponent.IComponent) => this.getStorage(instance).componentData$;
        const ngDestroy = (instance: NICmsContentComponent.IComponent) => this.ngDestroy(instance);
        const ngInit = (instance: NICmsContentComponent.IComponent) => this.ngInit(instance);
        const ngChanges = (instance: NICmsContentComponent.IComponent) => this.ngChanges(instance);

        Object.defineProperty(target, propertyKey, {
            get(): T | undefined {
                return getComponentData$(this).getValue();
            },
            set(data: T) {
                set(data, this);
            }
        });

        const nativeOnDestroy = target['ngOnDestroy'];
        Object.defineProperty(target, 'ngOnDestroy', {
            value: function () {
                nativeOnDestroy?.call(this);
                ngDestroy(this);
            },
            enumerable: false
        });
        const nativeOnInit = target['ngOnInit'];
        Object.defineProperty(target, 'ngOnInit', {
            value: function () {
                const instance: NICmsContentComponent.IComponent = this;
                ngInit(instance);
                const result = nativeOnInit?.call(instance);

                const cb = () => {
                    // trigger validation after ng on init again as rules might change case of that
                    if (instance.iCmsValidation$?.observed) {
                        instance.iCmsValidation$.next();
                    }
                    instance.initiated = true;
                };
                if (result instanceof Promise) {
                    result.finally(cb);
                } else {
                    cb();
                }
            },
            enumerable: false
        });
        const nativeOnChanges = target['ngOnChanges'];
        Object.defineProperty(target, 'ngOnChanges', {
            value: function (...args) {
                ngChanges(this);
                nativeOnChanges?.call(this, ...args);
            },
            enumerable: false
        });
        if (this.isCmsDataProperty()) {
            Object.defineProperty(target, '_setICmsContent', {
                value: function (content: NICmsContentComponent.IData): void {
                    this[propertyKey] = content;
                },
                enumerable: false
            });

            Object.defineProperty(target, '_icmsContent$', {
                get(): BehaviorSubject<T | undefined> {
                    return getComponentData$(this);
                }
            });
        }

        ProcessorService.onReady(() => {
            this.iCmsCoreIntegrationProviderService.registerEntry(this);
        });
    }

    protected getStorage(instance: NICmsContentComponent.IComponent): TStorage {
        let storage = this.storage.get(instance);
        if (!storage) {
            const subscription = new Subscription();
            const componentData$ = new BehaviorSubject<T | undefined>(undefined);
            const iCmsValidation$ = new Subject<IValidtionEvent | undefined>();
            storage = {
                componentData$,
                iCmsValidation$,
                subscription
            } as TStorage;
            this.storage.set(instance, storage);
            const isCmsDataProperty = this.isCmsDataProperty();
            let errorSubscription: Subscription;
            let hook: ObjectHook | undefined;

            if (isCmsDataProperty) {
                // when component triggers its own validation lets pass that to hook
                iCmsValidation$.subscribe(event => {
                    if (!event && hook) {
                        hook.trigger$.next();
                    }
                });
            }

            let oldValue: T | undefined;

            subscription.add(
                componentData$.subscribe(data => {
                    if (isCmsDataProperty) {
                        errorSubscription?.unsubscribe();
                        if (data) {
                            errorSubscription = new Subscription();
                            // pass validation to component
                            errorSubscription.add(
                                this.iCmsContentComponentValidationService.validation$.subscribe(event => {
                                    if (event.data === (data as any)) {
                                        iCmsValidation$.next(event);
                                    }
                                })
                            );

                            hook = this.iCmsContentComponentValidationService.getObjectHook(
                                data as any as NICmsContentComponent.IData,
                                true,
                                this.getMeta(),
                                instance.injector.get(NICmsContentComponent.OutletInstance, undefined, { optional: true })
                            );

                            errorSubscription.add(
                                hook!.state.subscribe(error => {
                                    const classList = this.iCmsCoreIntegrationComponentService.getInstanceElement(instance).classList;
                                    error ? classList.add('icmsHiddenComponent') : classList.remove('icmsHiddenComponent');
                                })
                            );
                        }
                    }
                    if (instance.initiated) {
                        iCmsValidation$.next(undefined); // trigger re-validation
                        instance.detectChanges({ [this.propertyKey]: new NICmsContentComponent.Change(data, oldValue) });
                    }
                    oldValue = data;
                })
            );
            subscription.add(
                this.iCmsDataChangeService.afterChange$.subscribe(event => {
                    this.onChange(event, instance);
                })
            );
        }
        return storage;
    }

    private ngDestroy(instance: NICmsContentComponent.IComponent) {
        if (this.isCmsDataProperty()) {
            this.iCmsCoreIntegrationComponentService.unregisterEntry(instance);
        }
        this.storage.get(instance)?.subscription?.unsubscribe();
        this.storage.delete(instance);
    }

    protected ngInit(instance: NICmsContentComponent.IComponent) {
        if (this.isCmsDataProperty()) {
            instance.iCmsValidation$ = this.getStorage(instance).iCmsValidation$;
            this.iCmsCoreIntegrationComponentService.registerEntry(instance);
        }
    }

    private isCmsDataProperty(): boolean {
        return ['blockContent', 'component', 'componentOrBlockContent'].includes(this.contentMeta.type!);
    }

    protected getMeta(): NICmsContentComponent.IMeta | undefined {
        return undefined;
    }
    protected abstract ngChanges(instance: NICmsContentComponent.IComponent): void;

    protected abstract onChange(event: NICmsDataChange.IChangeDataEvent, instance: NICmsContentComponent.IComponent): void;

    protected abstract setContent(data: T, componentInstance: NICmsContentComponent.IComponent): void;
}
